import type { ReactElement, ReactNode } from 'react';

/**
 * The one shape every state that is not a list takes.
 *
 * Written once because the states differ by a colour, an icon and two sentences;
 * hand-built panels would differ by a padding as well, and a screen would look
 * like several screens. It became a component of its own when reminders arrived:
 * two lists each with their own copy is exactly how the empty state of one comes
 * to sit two pixels off the empty state of the other.
 *
 * `OfflineNotice` carries the same shape, because it is shown outside a list as
 * well — the two are meant to look like the same thing, and do.
 */
export function Panel({
  tone,
  title,
  body,
  icon,
  action,
  dashed = false,
}: {
  tone: 'quiet' | 'alert' | 'accent';
  title: string;
  body: string;
  /** The **contents** of an `<svg viewBox="0 0 24 24">`, not the element. */
  icon: ReactNode;
  action?: ReactNode;
  dashed?: boolean;
}): ReactElement {
  const colours = {
    quiet: 'border-line-strong bg-surface/60 text-ink-400',
    alert: 'border-line-strong bg-alert-soft text-alert',
    accent: 'border-line-strong bg-accent-soft text-accent',
  }[tone];

  return (
    <div
      className={`mt-4 rounded-lg border px-5 py-8 text-center ${dashed ? 'border-dashed' : ''} ${colours}`}
      // Announced when it replaces the list, since the reason the list is not
      // there is exactly what it says.
      role={tone === 'alert' ? 'alert' : undefined}
    >
      <svg
        viewBox="0 0 24 24"
        className="mx-auto size-7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {icon}
      </svg>
      <p className="mt-3 font-medium text-ink-900">{title}</p>
      <p className="mx-auto mt-1 max-w-prose text-sm text-ink-500">{body}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
